/**
 * Mountain drawings — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_drawings`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const mountainDrawingsPack: ToilePack = {
    slug: 'mountain-drawings',
    category: 'illustration',
    name: 'Mountain drawings',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 1216, h: 832},
    readings: [
        {key: 'peaks', label: 'Alpine peaks', subject: 'a jagged alpine ridge with snow in the gullies and clouds lying below the summits'},
        {key: 'cabin', label: 'Mountain cabin', subject: 'a log cabin at the foot of a mountain, smoke from the chimney and pines climbing the slope'},
        {key: 'ranges', label: 'Layered ranges', subject: 'layered mountain ranges receding into haze, each ridge paler than the one before it'},
    ],
    styles: [
        {key: 'pencil', label: 'Pencil', style: 'graphite pencil drawing, soft layered shading from sharp 2H detail to deep 6B darks, visible paper tooth', palette: 'graphite gray only', framing: 'on plain white paper, the whole sheet used, a thin white margin around the drawing'},
        {key: 'ink', label: 'Pen and ink', style: 'pen and ink drawing, confident contour lines, fine crosshatching and stippling for tone', palette: 'black ink only', framing: 'on clean white paper, centered, a white margin all round'},
        {key: 'charcoal', label: 'Charcoal', style: 'charcoal drawing, smudged tonal masses with crisp accents and lifted highlights', palette: 'black charcoal on white', framing: 'on white drawing paper, the subject filling the sheet, a white edge left clear'},
    ],
    directions: ['a lake', 'winter', 'a climber'],
    directionHint: 'try: a lake',
    seo: {
        title: 'Mountain Drawings: Alpine Peaks, Cabin, Layered Ranges',
        metaDescription: 'Nine mountain drawings: an alpine ridge above the clouds, a log cabin under a slope and layered ranges fading into haze, in three media.',
        h1: 'Mountain drawings: an alpine ridge, a log cabin, layered ranges',
        tagline: 'Three mountain views, each drawn in graphite pencil, pen and ink, and charcoal.',
        intro: 'Three mountain views: a jagged alpine ridge with snow in the gullies, a log cabin at the foot of a slope, and layered ranges fading into haze. Each is drawn in pencil, pen and ink, and charcoal, so the mass of the mountain and the air between ridges compare across the media. Type a different mountain or a twist, such as a lake or winter, and the same media carry it.',
        useCases: [
            'a sketchbook study of ridgelines',
            'a framed print for a hallway',
            'a reference for atmospheric perspective',
            'a cabin scene for a card',
        ],
        faq: [
            {q: 'What is in this pack?', a: 'Nine drawings of three mountain views: an alpine ridge above the clouds, a log cabin under a slope, and layered ranges in haze. Each view appears in three media.'},
            {q: 'Can I ask for a different mountain scene?', a: 'Yes. Type a subject or twist, such as a lake, winter or a climber. A typed direction is a request, and the result may differ from the samples.'},
            {q: 'How do the three styles differ?', a: 'Pencil builds soft layered shading on plain white paper. Pen and ink uses confident contour lines with crosshatching. Charcoal uses smudged masses with crisp accents.'},
            {q: 'Is each drawing wide or tall?', a: 'Wide. The mountain drawings are wider than they are tall, which fits a ridge against the sky.'},
        ],
    },
}
